import { GrowProject, GrowProjectStage, GrowProjectStatus, InyovaGrowPercentages, InyovaGrowRisk, groupRiskQuestions } from '@inyova/grow';
import { GrowAccount, InyovaGrowAccountStatus } from '@inyova/models';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as AppRootActions from '@app/app.actions';
import * as ProfileActions from '@profile/profile.actions';

import * as GrowActions from './grow-actions';

export const GROW_REDUCER_FEATURE_KEY = 'grow';
const interestedInvestment = {
  amount: null,
  amount_committed_at: null,
  documents_reviewed_at: null,
  general_conditions_accepted_at: null,
  waiver_agreed_at: null,
  risk_agreed_at: null,
  status: GrowProjectStatus.INTERESTED
};

export interface GrowState {
  growAccount: GrowAccount;
  growProjects: { [key: string]: GrowProject };
  growProjectsIds: string[];
  growProjectsLoading: boolean;
  growProjectsLoaded: boolean;
  growRisk: null | InyovaGrowRisk;
  growPercentages: null | InyovaGrowPercentages;
  growIntroLoading: boolean;
  growRiskLoading: boolean;
  growRiskLoaded: boolean;
  growCheckLoading: boolean;
  growSubmitLoading: boolean;
  displayLanguage: string;
}

export const initialState: GrowState = {
  growAccount: {
    id: '',
    kind: 'grow',
    created_at: '',
    currency: 'CHF',
    customer_journey: {
      account_opened: true,
      account_funded: true,
      id_verification_completed: true,
      customer_funded: true,
      compliance_done: true,
      account_data_filled: true,
      ql_flow_completed: true
    },
    onboarding_step: 0,
    ql_onboarding_step: 0,
    enabled_features: [],
    fees_consent: false,
    step_status: 0,
    initial_investment: null,
    monthly_investment: null,
    stock_split: null,
    current_amount: 0,
    portfolio_value: null,
    portfolio_value_chf: null,
    portfolio_value_eur: null,
    portfolio_value_eur_chf: null,
    combined_expected_return: null,
    minimum_investment: 2000,
    iban: '',
    investment_risk_consent: false,
    owner_name: '',
    reporting_timestamp: '',
    inyova_and_custody_fees: null,
    product_costs: null,
    eur_exchange_rate: 0,
    deposit_qr_code: '',
    savings_plan_social_proof: null
  },
  growProjects: {},
  growProjectsIds: [],
  growProjectsLoading: false,
  growProjectsLoaded: false,
  growRisk: null,
  growPercentages: null,
  growIntroLoading: false,
  growRiskLoading: false,
  growRiskLoaded: false,
  growCheckLoading: false,
  growSubmitLoading: false,
  displayLanguage: ''
};

export const growReducer = createReducer(
  initialState,
  on(GrowActions.createGrowAccount, (state) => ({
    ...state,
    growIntroLoading: true
  })),
  on(GrowActions.createGrowAccountSuccess, (state, action) => ({
    ...state,
    growAccount: action.data,
    growIntroLoading: false
  })),
  on(GrowActions.createGrowAccountFail, (state) => ({ ...state, growAccount: null, growIntroLoading: false })),
  on(GrowActions.getGrowAccountSuccess, (state, action) => ({
    ...state,
    growAccount: action.data
  })),
  on(GrowActions.getGrowRisk, (state, { language }) => {
    return {
      ...state,
      growRiskLoaded: state.displayLanguage === language ? state.growRiskLoaded : false
    };
  }),
  on(GrowActions.getGrowRiskSuccess, (state, { data, percentages, language }) => {
    return {
      ...state,
      growRisk: data,
      growPercentages: percentages,
      growRiskLoading: false,
      growRiskLoaded: true,
      displayLanguage: language
    };
  }),
  on(GrowActions.getGrowRiskFail, (state) => {
    return {
      ...state,
      growRiskLoading: false,
      growRiskLoaded: false
    };
  }),
  on(GrowActions.checkGrowRiskProfile, (state) => {
    return {
      ...state,
      growCheckLoading: true
    };
  }),
  on(GrowActions.checkGrowRiskProfileSuccess, (state, { data }) => {
    return {
      ...state,
      growPercentages: { ...data },
      growCheckLoading: false
    };
  }),
  on(GrowActions.checkGrowRiskProfileFail, (state) => {
    return {
      ...state,
      growCheckLoading: false
    };
  }),
  on(GrowActions.submitGrowRisk, (state) => ({
    ...state,
    growSubmitLoading: true,
    ...(state.growAccount.step_status === InyovaGrowAccountStatus.FUNDED && { growProjectsLoaded: false })
  })),
  on(GrowActions.submitGrowRiskSuccess, (state) => {
    return {
      ...state,
      growSubmitLoading: false
    };
  }),
  on(GrowActions.submitGrowRiskFail, (state) => ({
    ...state,
    growSubmitLoading: false
  })),
  on(AppRootActions.switchAccount, ProfileActions.logout, () => ({ ...initialState })),
  on(GrowActions.markGrowProjectSuccess, (state, { projectId }) => {
    return {
      ...state,
      growProjects: {
        ...state.growProjects,
        [projectId]: {
          ...state.growProjects[projectId],
          investments: [interestedInvestment]
        }
      }
    };
  }),
  on(GrowActions.unmarkGrowProjectSuccess, (state, { projectId }) => {
    return {
      ...state,
      growProjects: {
        ...state.growProjects,
        [projectId]: {
          ...state.growProjects[projectId],
          investments: []
        }
      }
    };
  }),
  on(GrowActions.getGrowProjects, (state) => ({
    ...state,
    growProjectsLoading: true
  })),
  on(GrowActions.getGrowProjectsSuccess, (state, { projects, money_market_fund }) => {
    const growProjectsIds: string[] = [];
    const growProjects = projects.reduce((entities: { [key: string]: GrowProject }, project) => {
      growProjectsIds.push(project.id);
      return {
        ...entities,
        [project.id]: project
      };
    }, {});

    return {
      ...state,
      growProjects,
      growProjectsIds,
      growFund: money_market_fund,
      growProjectsLoading: false,
      growProjectsLoaded: true
    };
  }),
  on(GrowActions.getGrowProjectsFail, (state) => {
    return {
      ...state,
      growProjectsLoading: false,
      growProjectsLoaded: false
    };
  }),
  on(AppRootActions.setIsLanguageChange, (state) => ({
    ...state,
    growProjectsLoaded: false
  })),
  on(AppRootActions.switchAccount, ProfileActions.logout, () => ({ ...initialState }))
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function reducer(state: GrowState | undefined, action: Action) {
  return growReducer(state, action);
}

/*
 * SELECTORS
 * */
export const selectFeature = (state) => state.grow;
export const selectGrowAccount = createSelector(selectFeature, (state: GrowState) => state.growAccount);
export const selectGrowIntroLoading = createSelector(selectFeature, (state: GrowState) => state.growIntroLoading);

export const selectGrowRiskLoaded = createSelector(selectFeature, (state: GrowState) => state.growRiskLoaded);
export const selectGrowRisk = createSelector(selectFeature, (state) => state.growRisk);
export const selectGrowOnboardingId = createSelector(selectGrowRisk, (state) => state.id);
export const selectGrowRiskFormDetails = createSelector(selectGrowRisk, selectGrowAccount, (growRisk, growAccount) => {
  if (growRisk === null) {
    return { growAccount, surveyGroups: null, financialDetail: null };
  }
  return {
    growAccount,
    surveyGroups: groupRiskQuestions(growRisk.risk_survey.survey_questions),
    financialDetail: growRisk.financial_detail
  };
});
export const selectGrowPercentages = createSelector(selectFeature, (state) => state.growPercentages);
export const selectRiskCheckLoading = createSelector(selectFeature, (state) => state.growCheckLoading);
export const selectRiskSubmitLoading = createSelector(selectFeature, (state) => state.growSubmitLoading);

export const isProjectMarkedForInterest = (projectId: string) =>
  createSelector(selectGrowProjects, (growProjects) => {
    return growProjects[projectId]?.investments.some(({ status }) => status === GrowProjectStatus.INTERESTED);
  });

export const selectGrowProjectsLoaded = createSelector(selectFeature, (state: GrowState) => state.growProjectsLoaded);
export const selectGrowProjects = createSelector(selectFeature, (state: GrowState) => state.growProjects);
export const selectGrowProjectsIds = createSelector(selectFeature, (state: GrowState) => state.growProjectsIds);

export const selectInvestableGrowProjects = createSelector(selectGrowProjects, (entities) =>
  Object.values(entities).filter((project) => project.stage === GrowProjectStage.INVESTABLE)
);

export const selectHasSelectedProject = createSelector(selectGrowProjects, (entities) => {
  return Object.values(entities).some((project) => project.investments.some((investment) => investment.status === GrowProjectStatus.INTERESTED));
});

export const selectSelectedProjects = createSelector(selectGrowProjects, (entities) => {
  return Object.values(entities).filter((project) => project.investments.some((investment) => investment.status === GrowProjectStatus.INTERESTED));
});

export const selectGrowProjectById = (id: string) =>
  createSelector(selectGrowProjects, (entities) => {
    return entities[id];
  });
